import type { SubjectLineCondition as SubjectLineConditionType } from "@redotech/redo-flows-common/rules/schemas";
import { QueryOperator } from "@redotech/redo-flows-common/rules/types";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";
import { commitPendingTagInput } from "../../commit-pending-tag-input";

export const SubjectLineCondition = memo(function SubjectLineCondition({
  condition,
  onChange,
  editableFields,
}: {
  condition: SubjectLineConditionType;
  onChange: (updated: SubjectLineConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const isEditable = (f: string) =>
    editableFields === undefined || editableFields.has(f);
  const queryStringsEditable = isEditable("queryStrings");
  const caseSensitiveEditable = isEditable("caseSensitive");
  const allowSubstringEditable = isEditable("allowSubstring");
  // Substring vs whole-word only affects "contains" matching; "is exactly" is a
  // pure equality check, so the option is irrelevant there.
  const operator = condition.queryOperator ?? QueryOperator.CONTAINS;
  const showAllowSubstring =
    operator === QueryOperator.CONTAINS ||
    operator === QueryOperator.DOES_NOT_CONTAIN;

  const commitPendingInput = () => {
    if (!queryStringsEditable) {
      return;
    }
    const queryStrings = commitPendingTagInput(
      condition.queryStrings || [],
      inputValue,
    );
    if (!queryStrings) {
      return;
    }
    onChange({ ...condition, queryStrings });
    setInputValue("");
  };

  return (
    <Flex dir="column" gap="lg" w="full">
      <Flex
        style={
          queryStringsEditable
            ? undefined
            : { pointerEvents: "none", opacity: 0.6 }
        }
      >
        <RedoTagInputField
          delimiters={["newline"]}
          inputValue={inputValue}
          label={t("Subject text to match")}
          onBlur={commitPendingInput}
          placeholder={t("Enter subject text to match")}
          setInputValue={setInputValue}
          setTags={(queryStrings) => {
            if (!queryStringsEditable) {
              return;
            }
            const uniqueStrings = Array.from(new Set(queryStrings));
            onChange({ ...condition, queryStrings: uniqueStrings });
          }}
          state="default"
          tags={(condition.queryStrings || []).map((text) => ({ text }))}
        />
      </Flex>
      <RedoCheckbox
        disabled={!caseSensitiveEditable}
        label={t("Case sensitive")}
        setValue={(v) => {
          if (!caseSensitiveEditable) {
            return;
          }
          onChange({ ...condition, caseSensitive: v === true });
        }}
        size="sm"
        value={condition.caseSensitive || false}
      />
      {showAllowSubstring && (
        <RedoCheckbox
          description={t(
            'e.g. "order" also matches "orders", "reorder", "preorders"',
          )}
          disabled={!allowSubstringEditable}
          label={t("Allow substring matches")}
          setValue={(v) => {
            if (!allowSubstringEditable) {
              return;
            }
            onChange({ ...condition, allowSubstring: v === true });
          }}
          size="sm"
          value={condition.allowSubstring || false}
        />
      )}
    </Flex>
  );
});
